import React, { useCallback, useState } from "react";
import { MdEmail, MdLock } from "react-icons/md";
import {
  resendEmailVerification,
  verifyEmail,
} from "../services/authService";
import { getLanguageCopy } from "../services/language";

const VERIFICATION_TEXTS = {
  title: {
    es: "Verifica tu correo",
    en: "Verify your email",
    ru: "Verify your email",
    de: "E-Mail verifizieren",
  },
  description: {
    es: "Enviamos un codigo de 6 digitos a",
    en: "We sent a 6-digit code to",
    ru: "We sent a 6-digit code to",
    de: "Wir haben einen 6-stelligen Code gesendet an",
  },
  code: {
    es: "Codigo *",
    en: "Code *",
    ru: "Code *",
    de: "Code *",
  },
  codePlaceholder: {
    es: "Ingresa el codigo",
    en: "Enter the code",
    ru: "Enter the code",
    de: "Code eingeben",
  },
  codeRequired: {
    es: "Ingresa el codigo de 6 digitos.",
    en: "Enter the 6-digit code.",
    ru: "Enter the 6-digit code.",
    de: "Gib den 6-stelligen Code ein.",
  },
  verify: {
    es: "Verificar correo",
    en: "Verify email",
    ru: "Verify email",
    de: "E-Mail verifizieren",
  },
  verifying: {
    es: "Verificando...",
    en: "Verifying...",
    ru: "Verifying...",
    de: "Wird verifiziert...",
  },
  resend: {
    es: "Reenviar codigo",
    en: "Resend code",
    ru: "Resend code",
    de: "Code erneut senden",
  },
  resending: {
    es: "Reenviando...",
    en: "Resending...",
    ru: "Resending...",
    de: "Wird erneut gesendet...",
  },
  success: {
    es: "Correo verificado. Ya puedes iniciar sesion.",
    en: "Email verified. You can log in now.",
    ru: "Email verified. You can log in now.",
    de: "E-Mail verifiziert. Du kannst dich jetzt anmelden.",
  },
  resendSuccess: {
    es: "Codigo reenviado. Revisa tu correo.",
    en: "Code resent. Check your email.",
    ru: "Code resent. Check your email.",
    de: "Code erneut gesendet. Pruefe deine E-Mail.",
  },
  genericError: {
    es: "No se pudo verificar el correo. Intenta de nuevo.",
    en: "Could not verify email. Please try again.",
    ru: "Could not verify email. Please try again.",
    de: "E-Mail konnte nicht verifiziert werden. Bitte versuche es erneut.",
  },
  back: {
    es: "Volver",
    en: "Back",
    ru: "Back",
    de: "Zurueck",
  },
};

const EmailVerificationPanel = ({
  contact,
  language,
  onVerified,
  onBack,
  backLabel,
}) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);

  const t = useCallback(
    (key) => getLanguageCopy(language, VERIFICATION_TEXTS[key], key),
    [language]
  );

  const normalizedContact = String(contact || "").trim();

  const onSubmit = async (event) => {
    event.preventDefault();
    const normalizedCode = String(code || "").replace(/\D/g, "").slice(0, 6);
    if (normalizedCode.length !== 6) {
      setError(t("codeRequired"));
      return;
    }

    setError("");
    setStatusMessage("");
    setVerifying(true);

    try {
      const response = await verifyEmail({
        contact: normalizedContact,
        code: normalizedCode,
      });
      setStatusMessage(response?.data?.message || t("success"));
      if (onVerified) onVerified(response?.data);
    } catch (verificationError) {
      setError(
        verificationError?.response?.data?.message ||
          verificationError?.message ||
          t("genericError")
      );
    } finally {
      setVerifying(false);
    }
  };

  const onResend = async () => {
    if (resending || verifying) return;
    setError("");
    setStatusMessage("");
    setResending(true);

    try {
      const response = await resendEmailVerification({
        contact: normalizedContact,
      });
      setStatusMessage(response?.data?.message || t("resendSuccess"));
    } catch (resendError) {
      setError(
        resendError?.response?.data?.message ||
          resendError?.message ||
          t("genericError")
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <div>
      <div className="mb-5 rounded-md border border-[#8b5724]/70 bg-[#1a120b] p-4 text-[#f8f2e6]">
        <div className="mb-2 flex items-center gap-2 text-[#ffd66b]">
          <MdEmail />
          <h3 className="text-xl font-bold">{t("title")}</h3>
        </div>
        <p className="text-sm text-[#f8f2e6]/75">
          {t("description")}{" "}
          <span className="font-semibold text-white">{normalizedContact}</span>.
        </p>
      </div>

      <form onSubmit={onSubmit} noValidate>
        <div className="mb-4">
          <label className="block text-lg text-[#f8f2e6]/85">{t("code")}</label>
          <div className="flex items-center border-b border-[#8b5724] py-2">
            <MdLock className="mr-2 text-[#ffd66b]" />
            <input
              type="text"
              inputMode="numeric"
              name="code"
              value={code}
              onChange={(event) => {
                setCode(event.target.value.replace(/\D/g, "").slice(0, 6));
                if (error) setError("");
              }}
              className="h-10 w-full bg-transparent px-2 text-white focus:outline-none"
              required
              placeholder={t("codePlaceholder")}
              maxLength={6}
            />
          </div>
          {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
          {statusMessage && (
            <p className="mt-2 text-sm text-green-400">{statusMessage}</p>
          )}
        </div>

        <button
          type="submit"
          className={`w-full rounded-md bg-gradient-to-b from-[#ffd66b] to-[#f7a928] py-2 font-bold text-[#150d05] shadow-[0_10px_24px_rgba(247,181,49,0.24)] transition duration-200 hover:brightness-110 ${
            verifying || resending ? "cursor-not-allowed opacity-50" : ""
          }`}
          disabled={verifying || resending}
        >
          {verifying ? t("verifying") : t("verify")}
        </button>

        <button
          type="button"
          className={`mt-3 w-full rounded-md border border-[#ffd66b]/60 py-2 font-bold text-[#ffd66b] transition duration-200 hover:border-[#ffd66b] hover:text-white ${
            verifying || resending ? "cursor-not-allowed opacity-50" : ""
          }`}
          onClick={onResend}
          disabled={verifying || resending}
        >
          {resending ? t("resending") : t("resend")}
        </button>

        {onBack && (
          <button
            type="button"
            className="mt-4 w-full text-sm text-[#f8f2e6]/70 hover:text-[#ffd66b]"
            onClick={onBack}
            disabled={verifying || resending}
          >
            {backLabel || t("back")}
          </button>
        )}
      </form>
    </div>
  );
};

export default EmailVerificationPanel;
